<template>
  <div class="cpic-page">
    <article class="article-shell">
      <header class="article-header">
        <div class="article-topbar">
          <button type="button" class="topbar-btn topbar-btn--close" aria-label="关闭" @click="handleClose">
            <svg class="close-icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6L18 18M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            </svg>
          </button>
          <button type="button" class="topbar-btn topbar-btn--more" aria-label="更多">
            <span class="more-dot"></span>
            <span class="more-dot"></span>
            <span class="more-dot"></span>
          </button>
        </div>
        <h1 class="article-title">{{ title }}</h1>
        <div class="article-meta">
          <div class="meta-left">
            <div class="meta-top">
              <span class="account-name">{{ accountName }}</span>
              <span class="meta-time">{{ publishTime }}</span>
            </div>
            <div class="meta-bottom">
              <span class="meta-city">{{ city }}</span>
              <div class="meta-right">
                <button type="button" class="meta-action">
                  <img class="meta-action-icon-img" src="/posters/erji.png" alt="" aria-hidden="true">
                  <span>听全文</span>
                </button>
                <button type="button" class="meta-action">
                  <span class="meta-action-icon">☆</span>
                  <span>星标</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section v-if="showPoster" class="poster-wrap" :class="{ 'poster-wrap--long': isLongPoster }">
        <img :src="posterSrc" :alt="title" class="poster-image" @load="onImageLoad" />
      </section>

      <footer class="article-footer">
        <p>阅读 {{ readCount }}</p>
      </footer>
    </article>
  </div>
</template>

<script>
import { getCpicSettings } from '@/api'

export default {
  name: 'CpicPage',
  data() {
    return {
      posterNaturalWidth: 1,
      posterNaturalHeight: 1,
      settings: null,
      settingsLoaded: false
    }
  },
  computed: {
    cpicId() {
      const id = Number(this.$route.meta && this.$route.meta.cpicId)
      return Number.isInteger(id) && id >= 1 && id <= 20 ? id : 1
    },
    mergedSettings() {
      return this.settings || {}
    },
    title() {
      return this.mergedSettings.title || ''
    },
    accountName() {
      return this.mergedSettings.company || ''
    },
    publishTime() {
      return this.mergedSettings.time || ''
    },
    city() {
      return this.mergedSettings.city || ''
    },
    readCount() {
      return this.mergedSettings.readCount || ''
    },
    posterSrc() {
      return this.mergedSettings.image || ''
    },
    showPoster() {
      // 只展示后端返回的数据，未加载完成前不渲染
      if (!this.settingsLoaded) {
        return false
      }
      return Boolean(this.posterSrc)
    },
    isLongPoster() {
      if (!this.posterNaturalWidth || !this.posterNaturalHeight) {
        return false
      }
      return this.posterNaturalHeight / this.posterNaturalWidth > 1.6
    }
  },
  mounted() {
    this.fetchSettings()
  },
  watch: {
    '$route.path'() {
      this.settingsLoaded = false
      this.settings = null
      this.fetchSettings()
    }
  },
  methods: {
    async fetchSettings() {
      try {
        const { data } = await getCpicSettings(this.cpicId)
        if (data && typeof data === 'object') {
          this.settings = data
        }
      } catch (error) {
        // 后端不可达时保留 query/default 展示，避免页面空白
        this.settings = null
      } finally {
        this.settingsLoaded = true
      }
    },
    handleClose() {
      const wxBridge = window.WeixinJSBridge
      if (wxBridge && typeof wxBridge.call === 'function') {
        wxBridge.call('closeWindow')
        return
      }
      window.open('', '_self')
      window.close()
      setTimeout(() => {
        window.location.replace('about:blank')
      }, 120)
    },
    onImageLoad(event) {
      this.posterNaturalWidth = event.target.naturalWidth
      this.posterNaturalHeight = event.target.naturalHeight
    }
  }
}
</script>

<style scoped>
.cpic-page {
  background: #f5f6f8;
  min-height: 100vh;
  padding: 0;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Hiragino Sans GB', 'Noto Sans CJK SC', sans-serif;
  -webkit-text-size-adjust: 100%;
  font-synthesis: none;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.article-shell {
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  background: #fff;
  padding: 0 16px 24px;
  font-family: inherit;
}

.article-header {
  margin-bottom: 18px;
}

.article-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
  padding-top: 8px;
  padding-bottom: 4px;
}

.topbar-btn {
  border: none;
  background: transparent;
  color: #111111;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}

.topbar-btn--close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 30px;
  height: 30px;
  color: #111111;
}

.topbar-btn--more {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 700;
}

.more-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #111111;
  display: inline-block;
}

.article-title {
  color: #111827;
  font-size: 23px;
  line-height: 1.55;
  font-weight: 400;
}

.article-meta {
  margin-top: 20px;
}

.meta-left {
  min-width: 0;
}

.meta-top {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.meta-bottom {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.meta-time {
  color: #576477;
  font-size: 15px;
  font-weight: 400;
}

.meta-city {
  color: #8b97a8;
  font-size: 15px;
  font-weight: 400;
}

.meta-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.meta-action {
  border: none;
  background: transparent;
  color: #0b4a8f;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  font-weight: 400;
}

.meta-action-icon {
  font-size: 15px;
  line-height: 1;
  color: #0b4a8f;
}

.meta-action-icon-img {
  width: 16px;
  height: 16px;
  display: inline-block;
  flex: 0 0 auto;
  object-fit: contain;
}

.account-name {
  color: #0b4a8f;
  font-size: 18px;
  font-family: inherit;
  font-weight: 400;
}

.meta-dot {
  color: #c0c7d1;
}

.poster-wrap {
  width: min(100%, 560px);
  margin: 0 auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.poster-wrap--long {
  width: min(100%, 460px);
}

.poster-image {
  width: 100%;
  height: auto;
  display: block;
}

.article-footer {
  margin-top: 20px;
  color: #9aa5b1;
  font-size: 15px;
  font-weight: 400;
}

@media (max-width: 768px) {
  .article-shell {
    padding: 0 12px 18px;
  }

  .article-title {
    font-size: 24px;
    line-height: 1.5;
  }

  .article-meta {
    margin-top: 16px;
  }

  .meta-top {
    gap: 6px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .account-name {
    font-size: 16px;
  }

  .meta-time,
  .meta-city,
  .meta-action {
    font-size: 14px;
  }

  .meta-right {
    gap: 10px;
  }

  .topbar-btn {
    min-width: 36px;
    min-height: 36px;
    font-size: 22px;
    font-weight: 700;
  }

  .close-icon {
    width: 26px;
    height: 26px;
  }

  .topbar-btn--close {
    font-size: inherit;
  }

  .poster-wrap,
  .poster-wrap--long {
    width: 100%;
    border-radius: 8px;
  }

  .article-footer {
    font-size: 14px;
  }
}

@media (max-width: 420px) {
  .article-shell {
    padding: 0 10px 16px;
  }

  .article-topbar {
    margin-bottom: 18px;
    padding-top: 6px;
    padding-bottom: 2px;
  }

  .article-title {
    font-size: 22px;
  }

  .account-name {
    font-size: 15px;
  }

  .meta-time,
  .meta-city,
  .meta-action,
  .meta-action-icon,
  .article-footer {
    font-size: 13px;
  }

  .close-icon {
    width: 24px;
    height: 24px;
  }

  .topbar-btn--close {
    font-size: inherit;
  }

  .meta-action-icon-img {
    width: 14px;
    height: 14px;
  }


  .meta-bottom {
    gap: 10px;
  }
}
</style>
